import {
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Instagram,
  MessageCircle,
  Star,
} from "lucide-react";
import type { InferGetServerSidePropsType, NextPage } from "next";
import Head from "next/head";
import NextImage from "next/image";
import { useCallback, useState } from "react";
import { b } from "@/b";
import { HEADER_FRAGMENT } from "@/components/Header";
import { PageLayout } from "@/components/PageLayout";
import COACH_CASEY from "@/components/pages/meet-the-team/casey_lewandowski_headshot.png";
import COACH_SAM from "@/components/pages/meet-the-team/sam_bernhardt_headshot.png";
import NUTRITION_HERO from "@/img/nutrition_hero.jpg";
import NUTRITION_INCLUDES from "@/img/nutrition_includes.jpg";
import { popupMiddleware } from "@/sanity/popup-middleware";
import { getApolloSSRProps, useHydratedQuery } from "@/util/apollo-client";

const Nutrition2_Query = b.query("Nutrition2", (b) => [b.__fragment(HEADER_FRAGMENT)]);

const FORM_URL = "https://jmlg0nfbnmp.typeform.com/to/kcWdlIDc";
const CALCULATOR_URL = "https://eepurl.com/hYjVe5";
const QUESTION_FORM_URL =
  "https://forms.office.com/Pages/ResponsePage.aspx?id=DQSIkWdsW0yxEjajBLZtrQAAAAAAAAAAAANAAdnODoBUM1JTVDFNREVGR0hLQTc1TEpFRUI3U1JYTS4u";

const testimonials = [
  {
    name: "Delaney D.",
    review:
      "Words can't even describe how great Casey is! She has helped me gain my self-confidence back, helped me be aware of the choices I make, and has helped me tremendously with learning how to count and track my macros. My biggest goal was not only to lose weight but was also to keep my motivation strong and she has helped me exceed those goals.",
  },
  {
    name: "Matt A.",
    review:
      "Casey has helped me conquer my diagnosis. She has been able to educate me on various aspects while pushing forward to achieve my overall fitness goals. I have been able to learn how to adjust my diet to meet my fitness goals while staying adherent to my body's needs. The opportunity to work with Casey has been a blessing!",
  },
  {
    name: "Nicki B.",
    review:
      "Casey basically changed my life. She is so good at explaining WHY we do certain things which makes it easier to implement into everyday life. Not only did I see physical changes in my appearance, but I feel much more equipped to manage my nutrition and training. Most importantly, my confidence has increased significantly. 100/10!",
  },
  {
    name: "Mary K.",
    review:
      "With Sam's help, I have reached a point of food freedom that I never thought possible. I have found a love for my body that I dreamt I would reach someday. My life has been so positively influenced and impacted by Sam, Casey, and the Visionary team. I am truly forever grateful for all of it.",
  },
  {
    name: "Nick S.",
    review:
      "This program is amazing. The program taught me how to be comfortable with the food that I eat and how my body uses that on an everyday basis. Casey is responsible for laying the framework and foundation to build that personal growth. Continue to trust, continue to have fun, and you will discover a new version of yourself.",
  },
];

const includes = [
  "Personalized nutrition program",
  "Custom workout program with exercise demo videos",
  "6-week educational course",
  "Discounted Visionary Meals",
  "Macro-friendly recipes",
  "Personalized supplement regimen",
  "24/7 support & accountability",
  "Tools & resources for lifelong success",
];

const Nutrition2Page: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props,
) => {
  const { data } = useHydratedQuery(props);
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const active = testimonials[testimonialIdx];

  const openForm = useCallback(() => window.open(FORM_URL, "_blank"), []);

  return (
    <>
      <Head>
        <title>{`Nutrition Coaching | Visionary Meals`}</title>
        <meta
          name="description"
          content="Work with our expert dietitians and nutrition coaches to build a sustainable, healthy lifestyle. Your fittest, healthiest self starts here."
        />
      </Head>
      <PageLayout data={data} noPadding>
        {/* ---- Hero ---- */}
        <section className="max-w-6xl mx-auto px-6 py-12 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left: text */}
            <div>
              <h1 className="text-3xl lg:text-5xl font-bold text-brand-black leading-tight">
                Envision the
                <br />
                Best You
              </h1>
              <p className="text-base text-gray-500 mt-4 max-w-md leading-relaxed">
                Our Registered Dietitians & Nutrition Coaches will help you create not just a diet,
                but a healthy, sustainable lifestyle you actually enjoy.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-3 mt-8">
                <button
                  type="button"
                  onClick={openForm}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-brand-mint text-brand-black text-sm font-semibold hover:bg-brand-mint/90 transition-all cursor-pointer active:scale-[0.98]"
                >
                  Apply to Learn More
                  <ArrowRight className="size-4" />
                </button>
                <a
                  href={CALCULATOR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all no-underline"
                >
                  Free Calorie Calculator
                  <ExternalLink className="size-3.5" />
                </a>
              </div>
            </div>
            {/* Right: hero image */}
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
              <NextImage
                src={NUTRITION_HERO}
                alt="Visionary Meals coaching team"
                fill
                className="object-cover"
                priority
              />
            </div>
          </div>
        </section>

        {/* ---- What's Included ---- */}
        <section className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
              <NextImage src={NUTRITION_INCLUDES} alt="Coaching" fill className="object-cover" />
            </div>
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-brand-black">What's Included</h2>
              <p className="text-sm text-gray-500 mt-2">
                Everything you need for a complete transformation.
              </p>
              <ul className="mt-6 space-y-3">
                {includes.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="flex items-center justify-center size-6 rounded-full bg-brand-mint/20 shrink-0">
                      <Check className="size-3.5 text-gray-700" strokeWidth={3} />
                    </span>
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={openForm}
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-mint text-brand-black text-sm font-semibold hover:bg-brand-mint/90 transition-all cursor-pointer active:scale-[0.98]"
              >
                Get Started
                <ArrowRight className="size-4" />
              </button>
            </div>
          </div>
        </section>

        {/* ---- Video ---- */}
        <section className="bg-gray-50 py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl lg:text-3xl font-bold text-brand-black text-center mb-8">
              See the Results
            </h2>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-lg">
              <iframe
                className="w-full h-full"
                src="https://www.youtube-nocookie.com/embed/XODDFR5D4wA?si=S7C_dP5htFlcDH5Q"
                title="Visionary Meals Nutrition Coaching"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        {/* ---- Testimonials ---- */}
        <section className="max-w-4xl mx-auto px-6 py-16 lg:py-24">
          <h2 className="text-2xl lg:text-3xl font-bold text-brand-black text-center mb-10">
            What Our Clients Say
          </h2>
          <div className="rounded-2xl border border-gray-200 p-6 lg:p-8">
            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={`star-${String(i)}`} className="size-4 text-amber-400 fill-amber-400" />
              ))}
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">"{active.review}"</p>
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
              <p className="text-sm font-semibold text-brand-black">{active.name}</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setTestimonialIdx((i) => (i === 0 ? testimonials.length - 1 : i - 1))
                  }
                  className="flex items-center justify-center size-8 rounded-full border border-gray-200 text-gray-500 hover:text-brand-black hover:border-gray-300 transition-colors cursor-pointer"
                >
                  <ChevronLeft className="size-4" />
                </button>
                <span className="text-xs text-gray-400 tabular-nums w-10 text-center">
                  {testimonialIdx + 1}/{testimonials.length}
                </span>
                <button
                  type="button"
                  onClick={() => setTestimonialIdx((i) => (i + 1) % testimonials.length)}
                  className="flex items-center justify-center size-8 rounded-full border border-gray-200 text-gray-500 hover:text-brand-black hover:border-gray-300 transition-colors cursor-pointer"
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ---- Coaches ---- */}
        <section className="bg-gray-50 py-16 lg:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl lg:text-3xl font-bold text-brand-black text-center mb-10">
              Our Coaches
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  image: COACH_CASEY,
                  name: "Casey Lewandowski",
                  title: "Head Nutrition & Fitness Coach",
                  instagram: "caseynicole_rd",
                  bio: "Registered Dietitian with a passion for helping everyday people become the best version of themselves through proper fitness & nutrition. OSU grad, certified personal trainer, and your biggest cheerleader.",
                },
                {
                  image: COACH_SAM,
                  name: "Sam Bernhardt, RD, LD",
                  title: "Nutrition Coach",
                  instagram: "samb_therd",
                  bio: "Board Certified Registered Dietitian with a Sports Nutrition focus. Passionate about helping others understand how to have a healthy relationship with food and movement while optimizing their health.",
                },
              ].map((coach) => (
                <div
                  key={coach.name}
                  className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm"
                >
                  <div className="relative aspect-[3/4]">
                    <NextImage
                      src={coach.image}
                      alt={coach.name}
                      fill
                      className="object-cover object-[center_25%]"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-brand-black">{coach.name}</h3>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mt-0.5">
                      {coach.title}
                    </p>
                    <p className="text-sm text-gray-600 mt-3 leading-relaxed">{coach.bio}</p>
                    <a
                      href={`https://www.instagram.com/${coach.instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-4 text-sm text-gray-500 hover:text-brand-black transition-colors no-underline"
                    >
                      <Instagram className="size-4" />@{coach.instagram}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- CTA: Get Started ---- */}
        <section className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
          <div className="rounded-2xl bg-gradient-to-br from-brand-mint/20 to-white border border-brand-mint/20 p-8 lg:p-12 text-center">
            <h2 className="text-2xl lg:text-3xl font-bold text-brand-black">Ready to Transform?</h2>
            <p className="text-sm text-gray-600 mt-3 max-w-md mx-auto">
              Fill out a short, risk-free application and one of our coaches will be in touch to get
              you started.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
              <button
                type="button"
                onClick={openForm}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-all cursor-pointer active:scale-[0.98]"
              >
                Apply Now
                <ArrowRight className="size-4" />
              </button>
              <a
                href={CALCULATOR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all no-underline"
              >
                Free Calorie Calculator
                <ExternalLink className="size-3.5" />
              </a>
            </div>
          </div>
        </section>

        {/* ---- Questions ---- */}
        <section className="border-t border-gray-100 py-12">
          <div className="max-w-xl mx-auto px-6 text-center">
            <MessageCircle className="size-8 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-brand-black">Have Questions?</h3>
            <p className="text-sm text-gray-500 mt-1">Our coaches would love to hear from you.</p>
            <a
              href={QUESTION_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-brand-mint hover:text-brand-black transition-colors no-underline"
            >
              Submit a Question
              <ArrowRight className="size-3.5" />
            </a>
          </div>
        </section>
      </PageLayout>
    </>
  );
};

export default Nutrition2Page;

export const getServerSideProps = popupMiddleware(async (ctx) => {
  return { props: await getApolloSSRProps(Nutrition2_Query.document(), {}, ctx) };
});
